import React, { PureComponent, lazy, Suspense } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { withTranslation } from 'react-i18next';
import {
  LinkOutlined,
  HistoryOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';

import { connect } from '../../StateProvider';

import Loading from '../../common/Loading';
import ExtraHeader from './ExtraHeader';
import userSettingsActions from '../../../actions/userSettingsActions';
import Icon from '../../common/UI/Icon';

import styles from './extraPanel.less';

const TabLinkedData = lazy(() => import('../RecordBody/linkedDataTab/TabLinkedData'));
const TabChatController = lazy(() => import('../RecordBody/chatTab/TabChatController'));
const TabHistoryController = lazy(() => import('../RecordBody/historyTab/TabHistoryController'));
const TabManualController = lazy(() => import('../RecordBody/manualTab/TabManual'));

const TABS_IDS = {
  LINKS: 'links',
  CHAT: 'chat',
  HISTORY: 'history',
  MANUAL: 'manual',
};
const TAB_COMPONENTS = {
  [TABS_IDS.LINKS]: TabLinkedData,
  [TABS_IDS.CHAT]: TabChatController,
  [TABS_IDS.HISTORY]: TabHistoryController,
  [TABS_IDS.MANUAL]: TabManualController,
};

class ExtraPanel extends PureComponent {
  static propTypes = {
    catalogId: PropTypes.string,
    sceneId: PropTypes.string,
    recordId: PropTypes.string,
    isWebForm: PropTypes.bool,
    isLoading: PropTypes.func,
  };

  constructor() {
    super();
    this.state = {
      tabId: null
    };
  }

  componentDidMount() {
    this.setState({ tabId: this.props.defaultTabId })
  }

  componentDidUpdate(prevProps) {
    if (this.props.defaultTabId !== prevProps.defaultTabId) {
      this.setState({ tabId: this.props.defaultTabId })
    }
  }

  onTabChange = tabId => {
    const { catalogId } = this.props;

    // save setting
    userSettingsActions.updateUserSettings(['catalogs', catalogId, 'viewMode', 'form', 'extraPanel', 'tabId'], { tabId });

    // change tab
    this.setState({ tabId })
  }

  getBaseTabs = () => {
    const { catalog, record, t, withExtraPanel } = this.props;

    const isNew = record.get('isNew');
    const messagesCount = record.getIn(['chatOptions', 'messagesCount']);
    const newMessages = record.getIn(['chatOptions', 'newMessages']);


    let baseTabs = []

    if (!isNew) {
      baseTabs = baseTabs.concat([
        // LINKS
        Immutable.Map({
          id: TABS_IDS.LINKS,
          name: t(`record.tabs.${TABS_IDS.LINKS}`),
          icon: <LinkOutlined />,
          title: t(`record.tabs.${TABS_IDS.LINKS}`),
        }),
        // CHAT
        Immutable.Map({
          id: TABS_IDS.CHAT,
          name: t(`record.tabs.${TABS_IDS.CHAT}`) + (messagesCount ? ` ${messagesCount}` : ''),
          badge: !!newMessages,
          icon: (
            <Icon
              className={styles.iconTabs}
              type={`icon ${messagesCount > 0 ? 'communication-91' : 'communication-87'}`}
              style={{ verticalAlign: '-2px' }}
            />
          ),
          title: t(`record.tabs.${TABS_IDS.CHAT}`),
        }),
        // HISTORY
        Immutable.Map({
          id: TABS_IDS.HISTORY,
          name: t(`record.tabs.${TABS_IDS.HISTORY}`),
          icon: <HistoryOutlined className={styles.iconTabs} style={{ verticalAlign: '-3px' }} />,
          title: t(`record.tabs.${TABS_IDS.HISTORY}`),
        }),
      ])
    };

    // MANUAL
    const manual = catalog && catalog.get('manual');
    if (manual) {
      baseTabs.push(
        Immutable.Map({
          id: TABS_IDS.MANUAL,
          name: t(`record.tabs.${TABS_IDS.MANUAL}`),
          icon: <InfoCircleOutlined />,
          title: t(`record.tabs.${TABS_IDS.MANUAL}`),
        })
      )
    }

    return Immutable.fromJS(baseTabs);
  }

  render() {
    const {
      catalogId,
      catalog,
      sceneId,
      scene,
      record,
      hasBeenEdit,
      isWebForm,
      isLoading
    } = this.props;

    if (!record || !catalog) {
      return (
        <div className={styles.container}>
          <Loading fullHeight />
        </div>
      );
    }

    const tabs = this.getBaseTabs();

    if (!tabs.size) return null;

    let { tabId } = this.state;
    tabId = tabId || TABS_IDS.CHAT;
    if (!tabs.find(t => t.get("id") === tabId)) {
      tabId = tabs.get(0).get("id");
    }
    const Component = TAB_COMPONENTS[tabId];

    return (
      <div className={styles.container}>
        <div className={styles.data}>

          <ExtraHeader
            catalog={catalog}
            scene={scene}
            record={record}
            tabs={tabs}
            tabId={tabId}
            isWebForm={isWebForm}
            isLoading={isLoading}
            onTabChange={this.onTabChange}
          />

          {Component && (
            <Suspense fallback={<Loading />}>
              <Component
                sceneId={sceneId}
                catalogId={catalogId}
                catalog={catalog}
                record={record}
                isWebForm={isWebForm}
              />
            </Suspense>
          )}
        </div>
      </div>
    );
  }
}

export default connect(
  withTranslation()(ExtraPanel),
  {
    userSettings: ['userSettings', 'catalogs'],
  },
  (props, { userSettings }) => {
    const defaultTabId = userSettings.getIn([props.catalogId, 'viewMode', 'form', 'extraPanel', 'tabId', 'tabId']);
    return {
      defaultTabId,
      ...props,
    };
  },
);
